import React, { useEffect, useState } from 'react';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react';
import { MdExpandMore, MdSearch, MdAutoAwesome, MdCheck, MdLightbulb } from 'react-icons/md';
import clsx from 'clsx';
import { Box, Group, Popover, Stack, Text, ThemeIcon } from '@mantine/core';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import Button from '@srm/ui/button';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { ChatAIMode } from '@/models/chatai/ChatAIMode';

const HINT_REVEAL_DELAY_MS = 700;

interface IModeOption {
    id: ChatAIMode;
    label: string;
    sublabel: string;
    icon: React.ReactNode;
}

interface IProps {
    mode: ChatAIMode;
    onChange: (mode: ChatAIMode) => void;
    /** Доступен ли каждый режим (по конфигу). По умолчанию оба. */
    ragAvailable?: boolean;
    agentAvailable?: boolean;
    /** Disabled когда тред уже persisted или содержит сообщения — mode зафиксирован. */
    disabled?: boolean;
    disabledReason?: string;
}

const ALL_OPTIONS: IModeOption[] = [
    {
        id: 'rag',
        label: 'Поиск по БЗ',
        sublabel: 'Ответы из базы знаний',
        icon: <MdSearch size={17} aria-hidden />,
    },
    {
        id: 'agent',
        label: 'Агент',
        sublabel: 'Найдёт документы и задачи, создаст и изменит карточки',
        icon: <MdAutoAwesome size={17} aria-hidden />,
    },
];

export const ChatAIModeSelector: React.FC<IProps> = ({
    mode,
    onChange,
    ragAvailable = true,
    agentAvailable = true,
    disabled = false,
    disabledReason,
}) => {
    const [open, setOpen] = useState(false);
    const username = useTypedSelector((state) => state.auth.user?.username) ?? '';
    const [hintSeen, setHintSeen] = useLocalStorageState<boolean>(`user-${username}.chatAIModeHint`, false);
    const [hintOpen, setHintOpen] = useState(false);

    const options = ALL_OPTIONS.filter((o) =>
        (o.id === 'rag' && ragAvailable) || (o.id === 'agent' && agentAvailable),
    );
    const current = options.find((o) => o.id === mode) ?? options[0];
    const showHint = !hintSeen && !disabled && options.length > 1;

    useEffect(() => {
        if (!showHint) return;
        const t = window.setTimeout(() => setHintOpen(true), HINT_REVEAL_DELAY_MS);
        return () => window.clearTimeout(t);
    }, [showHint]);

    const dismissHint = () => {
        setHintOpen(false);
        setHintSeen(true);
    };

    useEffect(() => {
        if (open && hintOpen) dismissHint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: (next) => { if (!disabled) setOpen(next); },
        placement: 'bottom-start',
        middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context, { event: 'mousedown', enabled: !disabled });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: { open: 180, close: 120 },
        initial: { opacity: 0, transform: 'translateY(-4px) scale(0.96)' },
        open: { opacity: 1, transform: 'translateY(0) scale(1)' },
    });

    if (options.length <= 1) {
        // Один режим доступен — selector не нужен.
        return null;
    }

    const handlePick = (next: ChatAIMode) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(false);
        if (next !== mode) onChange(next);
    };

    return (
        <>
            <Popover
                opened={hintOpen}
                onClose={dismissHint}
                position="bottom-start"
                withArrow
                arrowSize={14}
                arrowOffset={22}
                arrowRadius={2}
                shadow="xl"
                radius="lg"
                offset={12}
                width={320}
                trapFocus={false}
                withinPortal
                zIndex={1300}
                classNames={{ dropdown: 'chat-ai-mode-selector__hint' }}
                styles={{
                    arrow: {
                        borderColor: 'rgba(91, 140, 255, 0.45)',
                        borderWidth: 1.5,
                        background: 'var(--hint-bg, #ffffff)',
                        boxShadow: '-2px -2px 6px -2px rgba(50, 70, 180, 0.10)',
                        zIndex: 2,
                    },
                }}
                transitionProps={{ transition: 'pop-top-left', duration: 240 }}
            >
                <Popover.Target>
                    <span className="chat-ai-mode-selector__hint-anchor">
                        <button
                            ref={refs.setReference}
                            {...getReferenceProps({
                                type: 'button',
                                disabled,
                                title: disabled ? disabledReason : 'Сменить режим чата',
                                'aria-label': `Режим: ${current?.label}. Нажмите чтобы сменить`,
                            })}
                            className={clsx('chat-ai-mode-selector__trigger', {
                                'chat-ai-mode-selector__trigger--disabled': disabled,
                                'chat-ai-mode-selector__trigger--hinted': hintOpen,
                            })}
                        >
                            <span className="chat-ai-mode-selector__icon" aria-hidden>{current?.icon}</span>
                            <span className="chat-ai-mode-selector__label">{current?.label}</span>
                            <MdExpandMore size={16} aria-hidden className="chat-ai-mode-selector__chevron" />
                        </button>
                    </span>
                </Popover.Target>
                <Popover.Dropdown>
                    <Group gap={10} align="flex-start" wrap="nowrap" mb={10}>
                        <ThemeIcon
                            size={36}
                            radius="md"
                            variant="gradient"
                            gradient={{ from: '#5b8cff', to: '#a26bff', deg: 135 }}
                            className="chat-ai-mode-selector__hint-badge"
                        >
                            <MdLightbulb size={18} />
                        </ThemeIcon>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={700} className="chat-ai-mode-selector__hint-title">
                                Сменить режим чата
                            </Text>
                            <Text size="xs" c="dimmed" mt={2}>
                                Здесь переключается режим ответов ИИ-ассистента.
                            </Text>
                        </Box>
                    </Group>
                    <Stack gap={8} mb={14}>
                        {ragAvailable && (
                            <Group gap={10} wrap="nowrap" align="flex-start" className="chat-ai-mode-selector__hint-row">
                                <ThemeIcon size={22} radius="sm" color="blue" variant="light">
                                    <MdSearch size={13} />
                                </ThemeIcon>
                                <Box>
                                    <Text size="xs" fw={600}>Поиск по БЗ</Text>
                                    <Text size="xs" c="dimmed" lh={1.35}>Точные ответы из вашей базы знаний</Text>
                                </Box>
                            </Group>
                        )}
                        {agentAvailable && (
                            <Group gap={10} wrap="nowrap" align="flex-start" className="chat-ai-mode-selector__hint-row">
                                <ThemeIcon size={22} radius="sm" color="violet" variant="light">
                                    <MdAutoAwesome size={13} />
                                </ThemeIcon>
                                <Box>
                                    <Text size="xs" fw={600}>Агент</Text>
                                    <Text size="xs" c="dimmed" lh={1.35}>Найдёт документы и задачи, создаст и изменит карточки</Text>
                                </Box>
                            </Group>
                        )}
                    </Stack>
                    <Group justify="flex-end">
                        <Button
                            variant="primary"
                            size="sm"
                            className="chat-ai-mode-selector__hint-cta"
                            onClick={dismissHint}
                        >
                            Понятно
                        </Button>
                    </Group>
                </Popover.Dropdown>
            </Popover>
            {isMounted && (
                <FloatingPortal>
                    <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 1200 }} {...getFloatingProps()}>
                        <div className="chat-ai-menu chat-ai-mode-selector__menu" style={transitionStyles}>
                            {options.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    className={clsx('chat-ai-menu__item chat-ai-mode-selector__item', {
                                        'chat-ai-mode-selector__item--active': opt.id === mode,
                                    })}
                                    onClick={handlePick(opt.id)}
                                >
                                    <span className="chat-ai-menu__item-icon" aria-hidden>{opt.icon}</span>
                                    <span className="chat-ai-mode-selector__item-text">
                                        <span className="chat-ai-menu__item-label">{opt.label}</span>
                                        <span className="chat-ai-mode-selector__item-sublabel">{opt.sublabel}</span>
                                    </span>
                                    {opt.id === mode && (
                                        <span className="chat-ai-mode-selector__check" aria-hidden>
                                            <MdCheck size={15} />
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};
